/**
 * useCreatePaymentIntent - POST /api/webapp/payment/intent (P5-D17-F9, T-F9-3).
 *
 * Mints a Stripe PaymentIntent for the held reservation and returns the 8-field
 * F5 contract (clientSecret + customerSessionClientSecret + connected account +
 * publishableKey + the reservation triple). Errors funnel through the shared
 * `mapCheckoutError`.
 *
 * The caller (CardCheckoutPanel, T-F9-5) owns the Idempotency-Key lifecycle:
 * generate it ONCE per client attempt and pass it in, so re-renders / refetches
 * / StrictMode double-invoke replay the SAME intent instead of minting a second
 * one (intent-once, Cross-Task Decision 6). This hook does NOT generate keys.
 */

import { useMutation } from '@tanstack/react-query';
import type { CreatePaymentIntentResponse } from '@/api/types';
import type { CheckoutEntry } from '@/types/checkout';
import { mapCheckoutError, type CheckoutError } from './checkoutError';

/**
 * Discriminated intent-creation input mirroring the route's exactly-one contract
 * (booking-detail-reskin D3): a `flow` entry sends `{flowToken, recipientToken?}`;
 * a `postApproval` entry sends `{bookingId}`. `recipientToken` stays flow-only.
 * The caller owns the Idempotency-Key lifecycle (generate ONCE per attempt).
 */
export type CreatePaymentIntentInput = CheckoutEntry & {
  /** Client-generated UUID — generate ONCE per attempt, not inside the mutation. */
  idempotencyKey: string;
};

/**
 * Sends the intent request. Each side brings its own transport (shared-client-screens D2): the
 * Mini App's is `appPaymentIntentRequest` (api/paymentIntent.ts, `apiFetch` on its session
 * token); the studio website sends it on its own session (web-sign-in-checkout D5).
 */
export type PaymentIntentRequest = (
  body: Record<string, unknown>,
  idempotencyKey: string,
) => Promise<CreatePaymentIntentResponse>;

export interface UseCreatePaymentIntentOptions {
  onSuccess?: (response: CreatePaymentIntentResponse) => void;
  onError?: (error: CheckoutError) => void;
  request: PaymentIntentRequest;
}

export function useCreatePaymentIntent(options: UseCreatePaymentIntentOptions) {
  const { onSuccess, onError, request } = options;

  return useMutation<CreatePaymentIntentResponse, CheckoutError, CreatePaymentIntentInput>({
    // Idempotency key must be stable across retries — the caller controls retry.
    retry: false,

    mutationFn: async (input) => {
      // Discriminated body: exactly one of flowToken/bookingId (route contract).
      // studio-shop D6: a shop order's route names the order in its path; its transport adds the checkout's claim.
      const body =
        input.kind === 'shopOrder'
          ? {}
          : input.kind === 'flow'
            ? { flowToken: input.flowToken, ...(input.recipientToken && { recipientToken: input.recipientToken }) }
            : { bookingId: input.bookingId };
      try {
        return await request(body, input.idempotencyKey);
      } catch (error) {
        throw mapCheckoutError(error);
      }
    },

    onSuccess: (response) => onSuccess?.(response),
    onError: (error) => onError?.(error),
  });
}
